/**
 * Lado do TechCambio no chat de suporte embutido: o portal do DeepDesk dentro
 * de um iframe, conversando com esta pagina por postMessage. Sem Vue, para o
 * Vitest cobrir.
 *
 * Contrato das mensagens: deepdesk/docs/widget-protocolo.md.
 *
 *   iframe: ready            -> host: init { userKey, locale, route, open }
 *   iframe: authenticated       (a sessao guardada no iframe ainda vale)
 *        ou need-assertion   -> host: assertion { token } -> authenticated | denied
 *
 * A sessao do suporte e do DeepDesk e fica dentro do iframe: o TechCambio nunca
 * a ve. Daqui so sai a assercao de 60 s, a mesma do menu "Suporte".
 */

export const WIDGET_MESSAGE_PREFIX = 'deepdesk:';
export const WIDGET_PROTOCOL_VERSION = 1;
export const WIDGET_LOCALE = 'pt-BR';

/** connecting: o iframe ainda nao autenticou. O chat so vale em `ready`. */
export const WIDGET_STATUS = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  READY: 'ready',
  DENIED: 'denied'
};

/** Por que o chat nao abre. `unavailable` e passageiro. */
export const WIDGET_DENIAL = {
  ACCESS_DENIED: 'accessDenied',
  PRODUCT_NOT_CONFIGURED: 'productNotConfigured',
  ASSERTION_INVALID: 'assertionInvalid',
  ORIGIN_NOT_ALLOWED: 'originNotAllowed',
  UNAVAILABLE: 'unavailable'
};

const HOST_MESSAGE = {
  INIT: 'init',
  ASSERTION: 'assertion',
  VISIBILITY: 'visibility',
  NAVIGATE: 'navigate',
  LOGOUT: 'logout'
};

const WIDGET_HOME_PATH = '/';
const WIDGET_SHELL_PATH = '/app/widget';
const STATE_KEY = 'support-widget-tc';
const LAUNCHER_KEY = 'support-widget-launcher-tc';
const DENIAL_KEY = 'support-widget-denied-tc';
const LONG_DENIAL_MS = 6 * 60 * 60 * 1000;
const SHORT_DENIAL_MS = 5 * 60 * 1000;
const LONG_DENIALS = [
  WIDGET_DENIAL.ACCESS_DENIED,
  WIDGET_DENIAL.PRODUCT_NOT_CONFIGURED,
  WIDGET_DENIAL.ORIGIN_NOT_ALLOWED
];

/* O host so conhece as telas do painel: inicio, conversas (lista, nova, uma
   conversa) e ajuda (lista, um artigo). Mesma regra do lado do DeepDesk. */
const WIDGET_ROUTE = /^\/(?:inicio|conversas(?:\/(?:nova|[0-9a-fA-F-]{36}))?|ajuda(?:\/[a-z0-9][a-z0-9-]{0,120})?)$/;

const IDLE_SNAPSHOT = { status: WIDGET_STATUS.IDLE, open: false, unread: 0, attention: 0 };

export function isWidgetRoute(route) {
  return typeof route === 'string' && WIDGET_ROUTE.test(route);
}

export function widgetConversationRoute(uuid) {
  return `/conversas/${uuid}`;
}

/** Endereco do iframe: a casca do chat no portal do produto. */
export function widgetFrameUrl(portalOrigin) {
  return `${portalOrigin}${WIDGET_SHELL_PATH}`;
}

export function idleSnapshot() {
  return { ...IDLE_SNAPSHOT };
}

function readJson(storage, key) {
  try {
    const raw = storage().getItem(key);

    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeJson(storage, key, value) {
  try {
    storage().setItem(key, JSON.stringify(value));
  } catch (error) {
    // eslint-disable-next-line no-empty
  }
}

function removeKey(storage, key) {
  try {
    storage().removeItem(key);
  } catch (error) {
    // eslint-disable-next-line no-empty
  }
}

const sessionStore = () => window.sessionStorage;
const localStore = () => window.localStorage;

/**
 * Painel aberto ou fechado e a tela em que a pessoa estava. Fica em
 * sessionStorage (morre com a aba) para o painel voltar igual depois de uma
 * recarga. So vale para o mesmo usuario: outra pessoa na mesma aba comeca do
 * zero.
 */
export function readWidgetState(userKey) {
  const stored = readJson(sessionStore, STATE_KEY);

  if (stored?.userKey !== userKey) return { open: false };

  return { open: stored.open === true, route: isWidgetRoute(stored.route) ? stored.route : undefined };
}

export function writeWidgetState(userKey, state) {
  writeJson(sessionStore, STATE_KEY, { userKey, open: state.open, route: state.route });
}

export function clearWidgetState() {
  removeKey(sessionStore, STATE_KEY);
  removeKey(sessionStore, LAUNCHER_KEY);
}

/**
 * A bolinha do chat no canto da tela: aparece quando a pessoa abre o suporte
 * pelo menu, ou quando chega resposta, e fica ate ela dispensar. Guardada por
 * aba, como o painel, e so para o mesmo usuario.
 */
export function readLauncher(userKey) {
  return readJson(sessionStore, LAUNCHER_KEY)?.userKey === userKey;
}

export function writeLauncher(userKey, isShown) {
  if (isShown) writeJson(sessionStore, LAUNCHER_KEY, { userKey });
  else removeKey(sessionStore, LAUNCHER_KEY);
}

/* Direito a suporte (contrato, liberacao) nao muda de uma hora para outra: quem
   foi recusado nao refaz a assercao a cada pagina. O que e passageiro volta a
   tentar logo. */
export function rememberDenial(userKey, reason, now = Date.now()) {
  const ttl = LONG_DENIALS.includes(reason) ? LONG_DENIAL_MS : SHORT_DENIAL_MS;

  writeJson(localStore, DENIAL_KEY, { userKey, reason, until: now + ttl });
}

export function readDenial(userKey, now = Date.now()) {
  const stored = readJson(localStore, DENIAL_KEY);

  if (!stored) return null;

  if (stored.userKey !== userKey || !(stored.until > now)) {
    removeKey(localStore, DENIAL_KEY);

    return null;
  }

  return stored.reason;
}

export function clearDenial() {
  removeKey(localStore, DENIAL_KEY);
}

function toCount(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function toText(value) {
  return value === null || value === undefined ? '' : String(value);
}

/* O que o iframe diz e o que a pagina faz com isso. `link` e o estado vivo da
   ponte, dividido com quem a criou. */
function createHandlers(link, options) {
  /* A assercao e de uso unico: um pedido por vez. */
  const answerAssertionRequest = async () => {
    if (link.isRequesting || link.isLoggedOut) return;

    link.isRequesting = true;

    try {
      const result = await options.requestAssertion();

      if (link.isLoggedOut) return;

      if (result?.token) link.send(HOST_MESSAGE.ASSERTION, { token: result.token });
      else link.deny(result?.reason || WIDGET_DENIAL.UNAVAILABLE);
    } catch (error) {
      if (!link.isLoggedOut) link.deny(WIDGET_DENIAL.UNAVAILABLE);
    } finally {
      link.isRequesting = false;
    }
  };

  return {
    /* Depois do logout o iframe avisa que esta pronto de novo; quem responde e
       a ponte do proximo usuario, nao esta. */
    ready: () => {
      if (link.isLoggedOut) return;

      link.send(HOST_MESSAGE.INIT, {
        userKey: options.userKey, locale: WIDGET_LOCALE, route: link.route, open: link.state.open
      });
    },
    'need-assertion': answerAssertionRequest,
    authenticated: (data) => {
      clearDenial();
      link.emit({ status: WIDGET_STATUS.READY, unread: toCount(data.unread), attention: toCount(data.attention) });
    },
    denied: (data) => link.deny(toText(data.reason) || WIDGET_DENIAL.UNAVAILABLE),
    unread: (data) => link.emit({ unread: toCount(data.count) }),
    attention: (data) => link.emit({ attention: toCount(data.count) }),
    /* Com o painel aberto naquela conversa, a mensagem ja esta na tela. */
    alert: (data) => {
      if (data.viewing === true || typeof data.uuid !== 'string') return;

      options.onAlert({
        uuid: data.uuid,
        ticketNumber: toText(data.ticketNumber),
        title: toText(data.title),
        authorName: toText(data.authorName),
        preview: toText(data.preview)
      });
    },
    state: (data) => {
      if (!isWidgetRoute(data.route)) return;

      link.route = data.route;
      link.persist();
    },
    close: () => link.setOpen(false),
    'open-portal': (data) => options.onOpenPortal(typeof data.path === 'string' ? data.path : WIDGET_HOME_PATH)
  };
}

/* Estado da ponte e as operacoes que as mensagens usam. */
function createLink(options) {
  const stored = readWidgetState(options.userKey);
  const link = {
    state: { status: WIDGET_STATUS.CONNECTING, open: stored.open, unread: 0, attention: 0 },
    route: stored.route,
    isRequesting: false,
    isLoggedOut: false
  };

  link.emit = (patch) => {
    link.state = { ...link.state, ...patch };
    options.onChange(link.state);
  };

  link.persist = () => writeWidgetState(options.userKey, { open: link.state.open, route: link.route });

  /* Sempre para a origem do portal, nunca "*". */
  link.send = (type, payload = {}) => {
    options.frame()?.postMessage(
      { ...payload, type: `${WIDGET_MESSAGE_PREFIX}${type}`, v: WIDGET_PROTOCOL_VERSION },
      options.portalOrigin
    );
  };

  link.deny = (reason) => {
    rememberDenial(options.userKey, reason);
    link.emit({ status: WIDGET_STATUS.DENIED, open: false, unread: 0, attention: 0 });
  };

  link.setOpen = (isOpen) => {
    link.emit({ open: isOpen });
    link.persist();
    link.send(HOST_MESSAGE.VISIBILITY, { open: isOpen });
  };

  return link;
}

/**
 * Uma ponte por sessao do widget (usuario + portal).
 *
 * options: {
 *   portalOrigin, userKey,
 *   frame: () => contentWindow do iframe,
 *   requestAssertion: async () => ({ token } | { reason }),
 *   onChange(snapshot), onAlert(alert), onOpenPortal(path)
 * }
 */
export function createWidgetBridge(options) {
  const link = createLink(options);
  const handlers = createHandlers(link, options);

  return {
    /* So o iframe do portal fala com esta pagina. */
    handleMessage(event) {
      const frame = options.frame();
      const data = event.data;
      const isFromPortal = !!frame && event.source === frame && event.origin === options.portalOrigin;

      if (!isFromPortal || typeof data?.type !== 'string' || !data.type.startsWith(WIDGET_MESSAGE_PREFIX)) return;

      handlers[data.type.slice(WIDGET_MESSAGE_PREFIX.length)]?.(data);
    },
    setOpen: link.setOpen,
    navigate(target) {
      if (isWidgetRoute(target)) link.send(HOST_MESSAGE.NAVIGATE, { route: target });
    },
    logout() {
      link.isLoggedOut = true;
      link.send(HOST_MESSAGE.LOGOUT);
      clearWidgetState();
      link.emit({ status: WIDGET_STATUS.CONNECTING, open: false, unread: 0, attention: 0 });
    },
    snapshot: () => link.state
  };
}
